/**
 * WorkspaceCoverageCard
 *
 * Per-jurisdiction rows using real per-jurisdiction doc counts from the v2 RPC
 * (jurisdiction_stats[]). Falls back to equal-split if jurisdiction_stats is
 * empty (pre-migration graceful degradation).
 *
 * Gap alerts from governing_bodies[] are shown as inline banners beneath
 * whichever jurisdiction they belong to, or at the bottom if unattributable.
 */

import React, { useState } from 'react';
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  X,
  Calendar,
  FileX,
  Building2,
} from 'lucide-react';
import type {
  CorpusStats,
  JurisdictionInfo,
  GoverningBodyStat,
} from '@/types/workspaceDetail';

// ── Gap Detail Modal ──────────────────────────────────────────────────────────

interface GapModalProps {
  gap: GoverningBodyStat;
  jurisdictionName: string;
  onClose: () => void;
}

function formatDate(iso: string | null): string {
  if (!iso) return 'Unknown';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  return Math.floor(
    (Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24)
  );
}

const GapDetailModal: React.FC<GapModalProps> = ({
  gap,
  jurisdictionName,
  onClose,
}) => {
  const days = daysSince(gap.last_meeting_date);

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 rounded-t-2xl border-b border-gray-100 bg-amber-50 px-5 py-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-100">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-amber-600">
                Coverage Gap
              </p>
              <p className="text-sm font-bold text-gray-900">
                {gap.name ?? 'Unknown Body'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 px-5 py-5">
          {/* Jurisdiction */}
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 flex-shrink-0">
              <Building2 className="h-4 w-4 text-gray-500" />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                Jurisdiction
              </p>
              <p className="text-sm font-medium text-gray-900">
                {jurisdictionName}
              </p>
            </div>
          </div>

          {/* Last meeting */}
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 flex-shrink-0">
              <Calendar className="h-4 w-4 text-gray-500" />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                Last Document On Record
              </p>
              <p className="text-sm font-medium text-gray-900">
                {formatDate(gap.last_meeting_date)}
                {days !== null && (
                  <span className="ml-2 text-xs font-normal text-amber-600">
                    ({days} days ago)
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Missing */}
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 flex-shrink-0">
              <FileX className="h-4 w-4 text-red-400" />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                Issue
              </p>
              <p className="text-sm font-medium text-gray-900">
                Minutes not ingested since{' '}
                {gap.last_meeting_date
                  ? new Date(gap.last_meeting_date).toLocaleDateString(
                      'en-US',
                      {
                        month: 'short',
                        year: 'numeric',
                      }
                    )
                  : 'the last known date'}
              </p>
            </div>
          </div>

          {/* Records on file */}
          <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1">
              Records On File
            </p>
            <p className="text-2xl font-bold text-gray-900">
              {gap.meeting_count.toLocaleString()}
            </p>
            <p className="text-xs text-gray-500">
              historical documents indexed for this body
            </p>
          </div>

          {/* Summary sentence */}
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 leading-relaxed">
            <strong>{gap.name ?? 'This body'}</strong> has not had new minutes
            ingested in over <strong>{days ?? '?'} days</strong>. Check the
            source site for recently published meeting records or verify the
            scraper is still targeting the correct URL.
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end border-t border-gray-100 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-gray-900 px-4 py-2 text-xs font-semibold text-white hover:bg-gray-700 transition-colors"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};

interface WorkspaceCoverageCardProps {
  stats: CorpusStats;
  jurisdictions: JurisdictionInfo[];
  loading: boolean;
}

const SkeletonRow: React.FC = () => (
  <div className="animate-pulse space-y-2 py-4">
    <div className="flex items-start justify-between">
      <div className="space-y-1">
        <div className="h-4 w-36 rounded bg-gray-200" />
        <div className="h-3 w-48 rounded bg-gray-100" />
      </div>
      <div className="h-4 w-10 rounded bg-gray-200" />
    </div>
    <div className="h-1.5 w-full rounded-full bg-gray-100" />
  </div>
);

export const WorkspaceCoverageCard: React.FC<WorkspaceCoverageCardProps> = ({
  stats,
  jurisdictions,
  loading,
}) => {
  const [openJurisdictions, setOpenJurisdictions] = useState<Set<string>>(
    new Set()
  );
  const toggleJurisdiction = (id: string) =>
    setOpenJurisdictions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  const [selectedGap, setSelectedGap] = useState<{
    gap: GoverningBodyStat;
    jurisdictionName: string;
  } | null>(null);
  const { total_docs, embedded_docs, jurisdiction_stats, governing_bodies } =
    stats;

  // ── Build per-jurisdiction display rows ───────────────────────────────────
  // Use real jurisdiction_stats if available, otherwise fall back to equal split.
  const jsMap = new Map(
    (jurisdiction_stats ?? []).map((js) => [js.jurisdiction_id, js])
  );

  type DisplayRow = {
    id: string;
    name: string;
    total: number;
    embedded: number;
  };

  let rows: DisplayRow[];

  if (jurisdictions.length === 0) {
    // No jurisdiction info — show single aggregate
    rows = [
      {
        id: 'all',
        name: 'All Documents',
        total: total_docs,
        embedded: embedded_docs,
      },
    ];
  } else if (jsMap.size > 0) {
    // Use real per-jurisdiction counts
    rows = jurisdictions.map((j) => {
      const js = jsMap.get(j.id);
      return {
        id: j.id,
        name: j.name,
        total: js?.total_docs ?? 0,
        embedded: js?.embedded_docs ?? 0,
      };
    });
  } else {
    // Fallback: equal split (pre-v2 migration)
    const split = jurisdictions.length;
    rows = jurisdictions.map((j) => ({
      id: j.id,
      name: j.name,
      total: Math.round(total_docs / split),
      embedded: Math.round(embedded_docs / split),
    }));
  }

  // Gap alerts — all gaps, and a lookup map: jurisdictionId → gap count
  const gaps: GoverningBodyStat[] = (governing_bodies ?? []).filter(
    (b) => b.has_gap
  );
  const gapsByJurisdiction = new Map<string, number>();
  for (const g of gaps) {
    if (g.jurisdiction_id) {
      gapsByJurisdiction.set(
        g.jurisdiction_id,
        (gapsByJurisdiction.get(g.jurisdiction_id) ?? 0) + 1
      );
    }
  }

  // Hide jurisdictions with zero documents — they add noise and no value
  const visibleRows = rows.filter((r) => r.total > 0);

  return (
    <>
      {/* ── Modal ───────────────────────────────────────────────────── */}
      {selectedGap && (
        <GapDetailModal
          gap={selectedGap.gap}
          jurisdictionName={selectedGap.jurisdictionName}
          onClose={() => setSelectedGap(null)}
        />
      )}

      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-widest text-gray-400">
          Document Coverage
        </p>

        {loading ? (
          <div className="divide-y divide-gray-100">
            <SkeletonRow />
            <SkeletonRow />
          </div>
        ) : total_docs === 0 ? (
          <p className="py-6 text-sm text-gray-400">
            No documents in scope yet. Documents appear here after ingestion.
          </p>
        ) : (
          <div className="divide-y divide-gray-100">
            {visibleRows.map((row) => {
              const pct =
                row.total > 0
                  ? Math.round((row.embedded / row.total) * 100)
                  : 0;
              const gapCount = gapsByJurisdiction.get(row.id) ?? 0;
              const rowGaps = gaps.filter((g) => g.jurisdiction_id === row.id);
              const listOpen = openJurisdictions.has(row.id);
              return (
                <div key={row.id} className="py-4 first:pt-2">
                  {/* Name row + pill + doc count */}
                  <div className="mb-2 flex items-start justify-between gap-4">
                    <div className="min-w-0 flex items-center gap-2">
                      <p className="text-sm font-semibold text-gray-900">
                        {row.name}
                      </p>
                      {/* Pill — toggles inline gap list for this place */}
                      {gapCount > 0 && (
                        <button
                          type="button"
                          onClick={() => toggleJurisdiction(row.id)}
                          className={[
                            'inline-flex items-center gap-1 rounded-full border px-2 py-0.5',
                            'text-[11px] font-medium transition-colors',
                            listOpen
                              ? 'border-amber-400 bg-amber-100 text-amber-800'
                              : 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100',
                          ].join(' ')}
                        >
                          <AlertTriangle className="h-3 w-3" />
                          <span>{gapCount}</span>
                          {listOpen ? (
                            <ChevronUp className="h-3 w-3" />
                          ) : (
                            <ChevronDown className="h-3 w-3" />
                          )}
                        </button>
                      )}
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <span className="text-sm font-semibold text-gray-900">
                        {row.total.toLocaleString()}
                      </span>
                      <p className="text-[11px] leading-none text-gray-400">
                        documents
                      </p>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                    <div
                      className="h-full rounded-full bg-blue-500 transition-all duration-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>

                  {/* Inline gap list — scrollable after 5 items */}
                  {listOpen && rowGaps.length > 0 && (
                    <div
                      className={[
                        'mt-2 space-y-1 overflow-y-auto rounded-lg border border-amber-100',
                        rowGaps.length > 5 ? 'max-h-[220px]' : '',
                      ].join(' ')}
                    >
                      {rowGaps.map((gap, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() =>
                            setSelectedGap({
                              gap,
                              jurisdictionName: row.name,
                            })
                          }
                          className="w-full flex items-center gap-2 bg-amber-50 px-3 py-2
                                     text-left hover:bg-amber-100 transition-colors group
                                     first:rounded-t-lg last:rounded-b-lg"
                        >
                          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-amber-500" />
                          <span className="flex-1 text-xs text-amber-800">
                            {gap.last_meeting_date
                              ? (() => {
                                  const d = new Date(gap.last_meeting_date);
                                  const mo = d.toLocaleDateString('en-US', {
                                    month: 'short',
                                  });
                                  const yr = d.getFullYear();
                                  return `${mo} ${yr} ${gap.name ?? ''} minutes missing`.trim();
                                })()
                              : `${gap.name ?? 'Meeting'} minutes missing`}
                          </span>
                          <span className="text-[10px] text-amber-400 group-hover:text-amber-600 transition-colors flex-shrink-0">
                            Details →
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};
